import React, { useCallback } from 'react';

import { AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onKnowledgeCardLinkClick } from '../../../knowledge-cards/analytics';

import { SainAction } from './hooks/sain-action';
import { useSAINUsageCriteriaMet } from './hooks/use-sain-usage';
import { useAnalytics } from './use-analytics';

/**
 * Maps native `peopleTeams` channel `actionSubjectId` values to SAIN
 * `aiAnswerDialog clicked` `location` attributes per the LDR:
 * Analytics Events for Mini People Profile UIs.
 *
 * Tab clicks are fired as a single `actionSubjectId: 'tab'` event with the
 * specific tab value (`recentWork` | `reportingLine` | `collaborators`) in
 * `payload.attributes.tab`; those are resolved separately below via
 * `USER_PROFILE_PREVIEW_TAB_LOCATION_MAP`.
 */
const PEOPLE_TEAMS_LOCATION_MAP: Record<string, Record<string, string>> = {
	userProfilePreview: {
		thirdPartyMessage: 'userProfilePreview-thirdPartyMessage',
		reportingLines: 'userProfilePreview-reportingLines',
		viewProfile: 'userProfilePreview-view-profile',
		recentWorkSection: 'userProfilePreview-recentWork',
		collaboratorsSection: 'userProfilePreview-collaborators',
	},
	userInlinePreview: {
		thirdPartyMessage: 'userInlinePreview-thirdPartyMessage',
		reportingLines: 'userInlinePreview-reportingLines',
		viewProfile: 'userInlinePreview-viewProfile',
	},
};

/**
 * Resolves a `userProfilePreview` `tab` click (single `actionSubjectId: 'tab'`)
 * to a SAIN `location` attribute based on `payload.attributes.tab`.
 */
const USER_PROFILE_PREVIEW_TAB_LOCATION_MAP: Record<string, string> = {
	recentWork: 'userProfilePreview-recentWorkTab',
	reportingLine: 'userProfilePreview-reportingLineTab',
	collaborators: 'userProfilePreview-collaboratorsTab',
};

const PEOPLE_TEAMS_ACTION_MAP: Record<string, Record<string, SainAction>> = {
	userProfilePreview: {
		thirdPartyMessage: SainAction.THIRD_PARTY_MESSAGE_CLICKED,
		reportingLines: SainAction.REPORTING_LINES_CLICKED,
		viewProfile: SainAction.VIEW_PROFILE_CLICKED,
	},
	userInlinePreview: {
		thirdPartyMessage: SainAction.THIRD_PARTY_MESSAGE_CLICKED,
		reportingLines: SainAction.REPORTING_LINES_CLICKED,
		viewProfile: SainAction.VIEW_PROFILE_CLICKED,
	},
};

type PeopleTeamsAnalyticsListenerProps = {
	children: React.ReactNode;
};

/**
 * Wraps `children` with an `AnalyticsListener` for the `peopleTeams` channel
 * and translates native click events fired by people-and-teams components
 * (e.g. `UserProfilePreview`, `UserInlinePreview`) into SAIN
 * `aiAnswerDialog clicked` track events plus matching SAIN usage (MAU) events.
 *
 * This abstraction is shared between the `PersonKnowledgeCard` and the
 * `SmartAnswerCardConsolidated` so that people-teams event instrumentation
 * stays consistent without duplicating the mapping or listener wiring.
 */
export const PeopleTeamsAnalyticsListener = ({
	children,
}: PeopleTeamsAnalyticsListenerProps): React.JSX.Element => {
	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const handlePeopleTeamsEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			const payload = event.payload as {
				action?: string;
				actionSubject?: string;
				actionSubjectId?: string;
				renderContext?: string;
				attributes?: { tab?: string };
			};
			if (payload.action !== 'clicked') {
				return;
			}

			let location: string | undefined;

			// `userProfilePreview` tab clicks are fired as a single `actionSubjectId: 'tab'`
			// event with the specific tab name in `payload.attributes.tab` (per
			// `FullVariant.handleTabChange`). Resolve via the dedicated tab map.
			if (payload.actionSubject === 'userProfilePreview' && payload.actionSubjectId === 'tab') {
				const tab = payload.attributes?.tab ?? '';
				location = USER_PROFILE_PREVIEW_TAB_LOCATION_MAP[tab];
			} else {
				const locationMap = PEOPLE_TEAMS_LOCATION_MAP[payload.actionSubject ?? ''];
				location = locationMap?.[payload.actionSubjectId ?? ''];
			}

			if (!location) {
				return;
			}

			const isCollaborator =
				payload.actionSubject === 'userInlinePreview' && payload.renderContext === 'collaborator';

			// `renderContext` is set directly on the event payload by `UserInlinePreview` when
			// it is rendered as a collaborator inside `UserProfilePreview`. Append it to the
			// SAIN `location` so collaborator clicks are distinguishable from standalone ones.
			if (isCollaborator) {
				location = `${location}-collaborator`;
			}

			// Fire the `aiAnswerDialog clicked` track event (existing location-based pattern).
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...commonAttributes,
					location,
				}),
			);

			// Fire the matching SAIN usage (MAU) event for parity with the existing person card
			// instrumentation in person-card-body.tsx. Tab/section toggles are UI-only and have
			// no pre-existing MAU criterion, so only action-intent clicks are mapped.
			const sainAction = isCollaborator
				? SainAction.COLLABORATOR_CLICKED
				: PEOPLE_TEAMS_ACTION_MAP[payload.actionSubject ?? '']?.[payload.actionSubjectId ?? ''];

			if (sainAction) {
				fireSAINUsageEvent({ metCriteria: sainAction, commonAttributes });
			}
		},
		[fireAnalyticsEvent, fireSAINUsageEvent, commonAttributes],
	);

	return (
		<AnalyticsListener channel="peopleTeams" onEvent={handlePeopleTeamsEvent}>
			{children}
		</AnalyticsListener>
	);
};
